/**
 * Referrals — referral-attribution dashboard (who referred whom).
 *
 * admin / coop_admin lands here from the sidebar and sees the referral roster
 * served by GET /referrals. admin sees every referral platform-wide;
 * coop_admin sees only referrals where the referred (new) user is a farmer in
 * a cooperative they admin. Read-only — a simple MUI table mirroring the
 * Farmers roster idiom (loading skeleton / error Alert+retry / empty Card).
 *
 * Localized via i18next (TD-072) — flat dotted keys under referrals.*.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  ListItem,
  Skeleton,
  Stack,
  StatCard,
  StatGrid,
  Text,
} from '@fse/ui';
import { useTranslation } from 'react-i18next';

import { useReferralsControllerList } from '../api/generated/referrals/referrals';
import type { ReferralDto } from '../api/generated/models';

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

export function ReferralsScreen() {
  const { t } = useTranslation();
  const referralsQuery = useReferralsControllerList();

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text variant="headline-medium" as="h2">
          {t('referrals.title')}
        </Text>
      </Box>

      {referralsQuery.isPending ? (
        <Card>
          <CardContent>
            <Stack spacing={1.5}>
              <Skeleton variant="rectangular" height={40} />
              <Skeleton variant="rectangular" height={40} />
              <Skeleton variant="rectangular" height={40} />
            </Stack>
          </CardContent>
        </Card>
      ) : referralsQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => referralsQuery.refetch()}>
              {t('referrals.retry')}
            </Button>
          }
        >
          {t('referrals.loadError', { error: String(referralsQuery.error) })}
        </Alert>
      ) : referralsQuery.data.length === 0 ? (
        <Card>
          <CardHeader title={t('referrals.empty.title')} />
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('referrals.empty.body')}
            </Text>
          </CardContent>
        </Card>
      ) : (
        <ReferralsTable rows={referralsQuery.data} />
      )}
    </Stack>
  );
}

interface ReferralsTableProps {
  rows: ReferralDto[];
}

function ReferralsTable({ rows }: ReferralsTableProps) {
  const { t } = useTranslation();
  // Presentational metrics derived from the already-fetched rows — no new data
  // fetching. Unique referrers = distinct referrerName values.
  const uniqueReferrers = new Set(rows.map((r) => r.referrerName)).size;

  return (
    <Stack spacing={2.5}>
      <StatGrid columns={2}>
        <StatCard
          label={
            rows.length === 1
              ? t('referrals.stat.referralsOne')
              : t('referrals.stat.referralsOther')
          }
          value={String(rows.length)}
          color="mango"
          icon="group_add"
        />
        <StatCard
          label={
            uniqueReferrers === 1
              ? t('referrals.stat.referrersOne')
              : t('referrals.stat.referrersOther')
          }
          value={String(uniqueReferrers)}
          color="leaf"
          icon="person"
        />
      </StatGrid>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {rows.map((referral, idx) => (
          <ListItem
            key={referral.referredUserId}
            leadingIcon="person_add"
            title={referral.referrerName}
            supportingText={t('referrals.row.supporting', {
              name: referral.referredName,
              date: dateFormatter.format(new Date(referral.referredAt)),
            })}
            noDivider={idx === rows.length - 1}
          />
        ))}
      </Box>
    </Stack>
  );
}
